import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface ShopIconProps {
    status: 'occupied' | 'selected' | 'hover' | 'neutral' | 'blocked' | 'competitor';
    team: 'A' | 'B' | 'none';
    className?: string;
}

const ShopIcon: React.FC<ShopIconProps> = ({ status, team, className = '' }) => {
    const { theme } = useTheme();

    // Define colors based on state
    let mainColor = '#94a3b8'; // Neutral Slate
    let roofColor = '#cbd5e1'; // Lighter Neutral
    let accentColor = '#64748b'; // Darker Neutral
    let windowColor = '#475569';
    let filter = 'none';

    if (team === 'A') {
        // BLUE TEAM (A)
        if (status === 'occupied') {
            mainColor = '#1e40af'; // Dark Blue (Solid)
            roofColor = '#3b82f6'; // Bright Blue
            accentColor = '#1d4ed8';
            windowColor = '#93c5fd';
            filter = 'drop-shadow(0 4px 6px rgba(30, 64, 175, 0.4))';
        } else if (status === 'selected') {
            mainColor = '#3b82f6'; // Neon Blue
            roofColor = '#60a5fa'; // Light Blue
            accentColor = '#2563eb';
            windowColor = '#dbeafe';
            filter = 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.8))';
        }
    } else if (team === 'B') {
        // RED TEAM (B)
        if (status === 'occupied') {
            mainColor = '#991b1b'; // Dark Red (Solid)
            roofColor = '#ef4444'; // Bright Red
            accentColor = '#b91c1c';
            windowColor = '#fca5a5';
            filter = 'drop-shadow(0 4px 6px rgba(153, 27, 27, 0.4))';
        } else if (status === 'selected') {
            mainColor = '#ef4444'; // Neon Red
            roofColor = '#f87171'; // Light Red
            accentColor = '#dc2626';
            windowColor = '#fee2e2';
            filter = 'drop-shadow(0 0 10px rgba(239, 68, 68, 0.8))';
        }
    }

    // Blocked (Conflict)
    if (status === 'blocked') {
        mainColor = '#b45309'; // Dark Orange
        roofColor = '#f59e0b'; // Amber
        accentColor = '#d97706';
        windowColor = '#fde68a';
        filter = 'drop-shadow(0 2px 4px rgba(180, 83, 9, 0.4))';
    }

    // Competitor (Game Master Block)
    if (status === 'competitor') {
        mainColor = '#6b21a8'; // Purple
        roofColor = '#a855f7'; // Light Purple
        accentColor = '#7e22ce';
        windowColor = '#d8b4fe';
        filter = 'drop-shadow(0 2px 4px rgba(107, 33, 168, 0.4))';
    }

    // Neutral / Hover
    if (status === 'hover') {
        mainColor = '#cbd5e1';
        roofColor = '#e2e8f0';
        filter = 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.6))';
    }

    // Theme adjustments for neutral
    if (status === 'neutral') {
        if (theme === 'dark') {
            mainColor = '#475569';
            roofColor = '#64748b';
            windowColor = '#334155';
        } else {
            mainColor = '#cbd5e1';
            roofColor = '#e2e8f0';
            windowColor = '#94a3b8';
        }
    }

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 61.97 63.76"
            className={className}
            style={{
                filter,
                transition: 'all 0.3s ease',
                opacity: status === 'blocked' ? 0.5 : 1
            }}
        >
            <defs>
                <clipPath id="shopClip">
                    <rect width="61.97" height="63.76" />
                </clipPath>
            </defs>

            <g>
                {/* Base Foundation */}
                <rect fill={mainColor} y="56.37" width="61.97" height="7.39" />

                {/* Pillars */}
                <rect fill={mainColor} x="56.25" y="22.76" width="2.62" height="34.8" />
                <rect fill={mainColor} x="3.93" y="22.76" width="2.62" height="34.8" />

                {/* Window Frames */}
                <rect fill={accentColor} x="10.49" y="28.01" width="20.5" height="2.62" />
                <rect fill={accentColor} x="9.45" y="46.44" width="24.15" height="2.62" />
                <rect fill={accentColor} x="10.49" y="28.01" width="2.98" height="19.74" />
                <rect fill={accentColor} x="28.01" y="28.01" width="2.98" height="19.74" />

                {/* Glass / Interior */}
                <rect fill={windowColor} x="13.47" y="30.63" width="14.54" height="15.81" opacity="0.6" />

                {/* Door */}
                <path fill={accentColor} d="M36.82,28.01v25.15h15.85v-25.15h-15.85ZM39.8,42.55h-1.55v-7.03h1.55v7.03Z" />
                <rect fill={windowColor} x="39.8" y="30" width="10" height="20" opacity="0.8" />

                {/* Roof */}
                <polygon fill={roofColor} points="61.97 23.48 0 23.48 8.82 0 51.25 0 61.97 23.48" />
                <rect fill={accentColor} y="13.94" width="61.97" height="9.54" opacity="0.3" />
            </g>
        </svg>
    );
};

export default ShopIcon;
